import React, { useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { usePopper } from 'react-popper';
import { styled } from '@linaria/react';

import { api } from '../../api';
import {
  ERROR_EMAIL_EMPTY,
  ERROR_EMAIL_INVALID,
  ERROR_EMAIL_LONG,
  LOGOUT_EVENT,
  MAX_LOGIN_LENGTH,
  USER_ROLE_CUSTOMER,
} from '../../constants';
import { useApi } from '../../hooks';
import { FieldSubmitError, Input, Label, LogoutButton } from '../../ui';
import { Info, Spinner, SuccessCheckmark } from '../../ui/icon';
import Tooltip from '../../ui/tooltip';
import { composeValidators } from '../../utils';
import { email, required } from '../../utils/validators';

const isRequired = required(ERROR_EMAIL_EMPTY);
const isEmail = email(ERROR_EMAIL_INVALID);
const isLogin = value => (value.length <= MAX_LOGIN_LENGTH ? undefined : ERROR_EMAIL_LONG);

const tooltipContent = {
  info: (
    <>
      <b>We will use your e-mail for:</b>
      <ul>
        <li>communication regarding your orders</li>
        <li>to send you invoices, and other billing info</li>
        <li>
          to provide you with marketing and promotional materials (if you give us permission to do
          so)
        </li>
      </ul>
    </>
  ),
  success:
    'Welcome back! Please go ahead and log into your account using your existing credentials.',
};

const LoginField = styled.div`
  position: relative;
`;
const IconContainer = styled.div`
  position: absolute;
  user-select: none;
  top: 50%;
  right: var(--bb-field-padding-left);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  & svg {
    pointer-events: none;
  }
  & svg {
    outline: none;
  }

  &:hover {
    cursor: help;
  }

  &:hover svg path,
  &:focus svg path {
    fill: var(--bb-icon-color--hover);
  }
`;

const Login = ({ domain, customer = {}, logoutHandler }) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [tooltipActive, setTooltipActive] = useState(false);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [10, 8],
        },
      },
    ],
  });

  const { mutators } = useForm();
  const { input, meta } = useField('login', {
    validate: composeValidators(isRequired, isEmail, isLogin),
    initialValue: customer.email,
    parse: value => (typeof value === 'string' ? value.trim() : value),
  });

  const [checking, setChecking] = useState(false);
  const [loginCached, setLogin] = useState();
  const authenticated = useMemo(() => !!customer.email, [customer.email]);
  const { input: loginAbility } = useField('login_ability');
  const { input: showGdprbyDefault } = useField('showGdprByDefault');
  const {
    body,
    meta: userCheckMeta,
    request,
  } = useApi(
    api(domain, 'userCheck'),
    {
      params: {
        login: input.value,
        user_role: USER_ROLE_CUSTOMER,
      },
    },
    false,
  );

  const onBlur = (...args) => {
    if (meta.valid && loginCached !== input.value) {
      setChecking(true);
      setLogin(input.value);
      request();
    }

    input.onBlur(...args);
  };

  const onChange = e => {
    if (loginAbility.value) {
      mutators.changeLoginAbility(false);
    }
    if (showGdprbyDefault.value) {
      mutators.toggleGdpr(true);
    } else {
      mutators.toggleGdpr(false);
    }
    mutators.clearSubmitError();

    setLogin(undefined);
    input.onChange(e);
  };

  const logout = () => {
    logoutHandler();
  };

  const handleLogout = () => {
    mutators.removeLogin();
    mutators.changeLoginAbility(false);
  };

  useEffect(() => {
    document.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => {
      document.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, []);

  useEffect(() => {
    const cleanup = () => {};
    if (!body) return cleanup;

    const { login_ability, marketing_agree_required, policy_agree_required } = body;
    mutators.changeLoginAbility(login_ability);
    mutators.toggleGdpr(policy_agree_required || marketing_agree_required);
    setChecking(false);

    return cleanup;
  }, [JSON.stringify(userCheckMeta)]);

  return (
    <>
      <Label>Email</Label>
      <LoginField>
        <Input
          placeholder="Enter your email"
          {...input}
          onBlur={onBlur}
          onChange={onChange}
          meta={meta}
          validate="submit"
          data-bb="login"
          disabled={!!customer.email}
        />

        {authenticated && (
          <LogoutButton
            type="button"
            onClick={logout}
            title="Log out"
            tabIndex="-1"
          />
        )}

        {!authenticated && (
          <>
            <IconContainer
              role="button"
              ref={setReferenceElement}
              onFocus={() => setTooltipActive(true)}
              onBlur={() => setTooltipActive(false)}
              onMouseEnter={() => setTooltipActive(true)}
              onMouseLeave={() => setTooltipActive(false)}
              tabIndex="-1"
              aria-label="Info"
            >
              {!checking && loginAbility.value && <SuccessCheckmark />}
              {!checking && !loginAbility.value && (
                <Info
                  data-tip
                  data-for="email-info"
                />
              )}
              {checking && (
                <Spinner
                  width="24"
                  height="24"
                />
              )}
            </IconContainer>

            <Tooltip
              className="bb-emailTooltip"
              tooltipRef={setPopperElement}
              style={styles.popper}
              arrowProps={{
                style: styles.arrow,
                ref: setArrowElement,
              }}
              isActive={tooltipActive}
              {...attributes.popper}
            >
              {loginAbility.value && tooltipContent.success}
              {!loginAbility.value && tooltipContent.info}
            </Tooltip>
          </>
        )}
      </LoginField>

      <FieldSubmitError meta={meta} />
    </>
  );
};

export default Login;
